import React, {useState} from 'react';
import {Box, Menu, MenuItem, Typography} from '@mui/material';

/**
 * Reusable menu field component for dropdown selections
 * Manages its own anchor state internally
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.field - Field name
 * @param {Object|string} props.value - Current value (object {value, label} or legacy string)
 * @param {Array} props.options - Array of {value, label} options
 * @param {Function} props.onSelect - Handler for selecting menu item
 */
export default function MenuField({
                                      label,
                                      field,
                                      value,
                                      options,
                                      onSelect
                                  }) {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (selectedOption) => {
        onSelect(field, selectedOption);
        handleClose();
    };

    const plainValue = value && typeof value.toJSON === 'function' ? value.toJSON() : value;

    // Extract display value from object/string, with robust fallback for partial shapes.
    const displayValue = typeof plainValue === 'object' && plainValue !== null
        ? (plainValue.label || plainValue.value || `Select ${label.toLowerCase()}`)
        : plainValue || `Select ${label.toLowerCase()}`;

    // Extract the actual value for comparison.
    const currentValue = typeof plainValue === 'object' && plainValue !== null
        ? plainValue.value
        : plainValue;
    const isEmpty = !currentValue;

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{minWidth: '150px', flexShrink: 0}}
            >
                {label}:
            </Typography>
            <Typography
                variant="body1"
                onClick={handleOpen}
                sx={{
                    color: isEmpty ? 'primary.main' : 'text.primary',
                    cursor: 'pointer',
                    minWidth: 0,
                    lineHeight: 1.5,
                    flex: 1,
                }}
            >
                {displayValue}
            </Typography>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: {
                            maxHeight: 400,
                            width: 440,
                        }
                    }
                }}
            >
                {options.map((option) => (
                    <MenuItem
                        key={option.value}
                        onClick={() => handleMenuItemClick(option)}
                        selected={currentValue === option.value}
                        sx={{whiteSpace: 'normal'}}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
}
